import {
  InlineDatumReactComponentProps,
  useAnalytics,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {useEffect, useState} from 'react';
import {view} from '@sail/ui';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '@stripe-internal/conversations-ui';
import {filter} from 'rxjs';
import {ResponseContent} from './ResponseContent';
import {
  inlineDataDoneCalled,
  streamingCompleteEvent,
  streamingFailedEvent,
  uiRenderingCompleteEvent,
} from './analytics';
import {StripeBrandResponse} from './brand/StripeBrandResponse';
import {SupportAIBrandResponse} from './brand/SupportAIBrandResponse';
import {StripeAssistantResponse} from './brand/StripeAssistantResponse';
import {SalesChatResponse} from './brand/SalesChatResponse';

export type LiveAnswersSource = {
  title?: string;
  subtitle?: string;
  collection?: string;
  url: string;
  score: number;
};

export type LiveAnswersResponseProps = {
  cache_uuid?: string;
  response?: string;
  sources?: Array<LiveAnswersSource>;
  answerable?: boolean;
  agent_escalation?: boolean;
  is_complete?: boolean;
  is_failure?: boolean;
  input_message?: string;
  streaming_conversation_id?: string;
};

// Poll the inline-data fetch endpoint every second while waiting for completion.
const POLL_MS = 1000;

// After 20 seconds without an initial response, retry.
const NO_INITIAL_RESPONSE_RETRY_MS = 20_000;

// If an initial response isn't received within 30 seconds or we do not get a complete response within 60 seconds, follow the failure branch.
const MAXIMUM_TIME_BEFORE_INITIAL_RESPONSE_MS = 30_000;
const MAXIMUM_TIME_FOR_COMPLETE_RESPONSE_MS = 60_000;

// These values MUST match with Opus::Support::InlineData::LiveAnswers::AbstractLiveAnswersResponse
const OUTCOME_ANSWERED = 'answered';
const OUTCOME_AGENT_ESCALATION = 'agent_escalation';
const OUTCOME_UNANSWERABLE = 'unanswerable';
const OUTCOME_INTERRUPTED = 'interrupted';

export const UniversalHelpLiveAnswersResponse = (
  props: InlineDatumReactComponentProps,
) => {
  const {datum, fetch, action, onDone, isDone} = props;
  const value = datum.value as LiveAnswersResponseProps;
  const {
    cache_uuid: cacheUuid,
    response,
    sources,
    answerable,
    agent_escalation: agentEscalation,
    is_complete: isComplete,
    is_failure: isFailure,
    input_message: inputMessage,
    streaming_conversation_id: streamingConversationId,
  } = value;

  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();
  const analytics = useAnalytics();
  const events = useInlineDataEvents();

  // Need to explicitly check for false because the prop can be nil
  // (which means we don't know if the question is answerable or not,
  // not that it's explicitly un-answerable)
  const unanswerable = answerable === false;

  // Tracks when this component started rendering which is used to track
  // if enough time has passed without any beginning response state
  // to trigger streamingFailed.
  const [uiRenderingStartTime, _] = useState<number>(() => +new Date());

  // Tracks if the UI rendering has completed, aka we are able to advance
  // to the next UH effect via onDone() because the WordStreamer has finished.
  const [uiRenderingComplete, setUiRenderingComplete] = useState(false);

  // Set when we reach the 20 second mark without an initial response
  const [noInitialResponseRetry, setNoInitialResponseRetry] = useState(false);

  // Set when we reach the 30 second mark without an initial response
  const [finalTimeoutRetry, setFinalTimeoutRetry] = useState(false);

  // Set to avoid UI jank when the client-side failure is triggered but we haven't yet re-rendered with the failure state
  const [clientSideFailure, setClientSideFailure] = useState(false);

  // The total number of fetch() calls made to the inline-data endpoint.
  const [fetchCallCount, setFetchCallCount] = useState(0);

  const [interruptNonce, setInterruptNonce] = useState<string | null>(null);

  // This tracks the backend streaming state and not the UI rendering.
  const streamingComplete =
    isComplete || unanswerable || isFailure || interruptNonce;

  // #1: When initially rendered, periodically fetch every POLL_MS while we are waiting for a complete response.
  // Until streamingComplete evaluates to true, we will trigger a new fetch call for each rerender from
  // the output of the previous call.
  useEffect(() => {
    if (isDone) {
      return;
    }

    if (interruptNonce || streamingComplete || !cacheUuid) {
      return;
    }

    const timeout = setTimeout(() => {
      if (interruptNonce || streamingComplete || !cacheUuid) {
        return;
      }

      setFetchCallCount((prevValue) => prevValue + 1);
      fetch({
        cache_uuid: cacheUuid,
      });
    }, POLL_MS);

    // if the first fetch() call fails, then the React component isn't re-rendered
    // and the useEffect hook isn't invoked again. In order to ensure we abort after
    // enough time has passed which is checked on effect of fetchCallCount changing,
    // we attempt an additional fetch at the NO_INITIAL_RESPONSE_RETRY_MS time.
    const noInitialResponseTimeout = setTimeout(() => {
      if (interruptNonce || streamingComplete || !cacheUuid) {
        return;
      }

      setFetchCallCount((prevValue) => prevValue + 1);
      setNoInitialResponseRetry(true);
      fetch({
        cache_uuid: cacheUuid,
      });
    }, NO_INITIAL_RESPONSE_RETRY_MS);

    const finalTimeout = setTimeout(
      () => {
        if (interruptNonce || streamingComplete || !cacheUuid) {
          return;
        }

        setFetchCallCount((prevValue) => prevValue + 1);
        setFinalTimeoutRetry(true);
        action({
          is_failure: true,
          cache_uuid: cacheUuid,
        });
      },
      noInitialResponseRetry
        ? MAXIMUM_TIME_BEFORE_INITIAL_RESPONSE_MS - NO_INITIAL_RESPONSE_RETRY_MS
        : MAXIMUM_TIME_BEFORE_INITIAL_RESPONSE_MS,
    );

    return () => {
      clearTimeout(timeout);
      clearTimeout(noInitialResponseTimeout);
      clearTimeout(finalTimeout);
    };
  }, [
    streamingComplete,
    cacheUuid,
    fetch,
    analytics,
    isDone,
    noInitialResponseRetry,
    action,
    interruptNonce,
  ]);

  // State which is set to true when streaming is complete to avoid repeated analytics calls.
  const [markedStreamingComplete, setMarkedStreamingComplete] = useState(false);

  // #2, success case: Full result is returned (with LLM response, or agent escalation/unanswerable).
  // Track in analytics but do not advance the conversation until the WordStreamer has finished
  // didsplaying the UI streaming effect to the user.
  useEffect(() => {
    if (isDone) {
      return;
    }

    if (
      streamingComplete &&
      !markedStreamingComplete &&
      !isFailure &&
      !interruptNonce
    ) {
      setMarkedStreamingComplete(true);
      const totalTime = Date.now() - uiRenderingStartTime;

      analytics.track(streamingCompleteEvent, {
        live_answers_agent_escalation: agentEscalation,
        live_answers_answerable: answerable,
        live_answers_complete: isComplete,
        live_answers_ui_rendering_total_time: totalTime,
      });
    }
  }, [
    agentEscalation,
    analytics,
    answerable,
    interruptNonce,
    isComplete,
    isDone,
    isFailure,
    markedStreamingComplete,
    streamingComplete,
    uiRenderingStartTime,
  ]);

  // State which is set to true when rendering is complete to avoid repeated analytics calls.
  const [markedRenderingComplete, setMarkedRenderingComplete] = useState(false);

  // #3, success case: When the WordStreamer has finished streaming text,
  // set uiRenderingComplete=true and trigger analytics once.
  const renderingCompleteCallback = (complete: boolean) => {
    setUiRenderingComplete(complete);
    if (complete && !markedRenderingComplete) {
      setMarkedRenderingComplete(true);
      const totalTime = Date.now() - uiRenderingStartTime;

      analytics.action(uiRenderingCompleteEvent, {
        live_answers_fetch_call_count: fetchCallCount,
        live_answers_agent_escalation: agentEscalation,
        live_answers_answerable: answerable,
        live_answers_complete: isComplete,
        live_answers_ui_rendering_total_time: totalTime,
      });
    }
  };

  // #4, success OR unanswerable OR agent escalation cases: pass the state
  // to the inline-data onDone() callback which advances the UH conversation.
  useEffect(() => {
    if (isDone) {
      return;
    }

    if (
      streamingComplete &&
      uiRenderingComplete &&
      !isFailure &&
      !interruptNonce
    ) {
      const totalTime = Date.now() - uiRenderingStartTime;

      let outcome = OUTCOME_ANSWERED;
      if (agentEscalation) {
        outcome = OUTCOME_AGENT_ESCALATION;
      } else if (unanswerable) {
        outcome = OUTCOME_UNANSWERABLE;
      }

      analytics.action(inlineDataDoneCalled, {
        live_answers_done_type: 'success',
        live_answers_done_outcome: outcome,
        live_answers_ui_rendering_total_time: totalTime,
      });

      const extra = {outcome};

      onDone({
        type: 'success',
        // TODO: stop passing the outcome as `message` once we switch over to using
        // inline_data_outcome and the new switch branch type on the backend.
        // On the backend, this overrides the input_message stored in scoped_data,
        // which we can then conditionally branch on using the boolean conditional
        // branch type and the live_answers_conditionals inline data's props.
        message: outcome,

        // TODO: update data to be the actual inline data's data prop, so
        //  the result can report the actual end value of the inline data
        //  since it could have changed since it was first rendered.
        data: extra,
        extra: {
          ...extra,

          // so that we can update the conversation without needing to
          // call in-progress, passing the value in the extra for now
          // until we can migrate the data prop
          value,
        },
      });
    }
  }, [
    streamingComplete,
    uiRenderingComplete,
    unanswerable,
    agentEscalation,
    onDone,
    analytics,
    answerable,
    isComplete,
    isFailure,
    isDone,
    value,
    uiRenderingStartTime,
    interruptNonce,
  ]);

  // #5, failure case: if we haven't received an initial response within MAXIMUM_TIME_BEFORE_INITIAL_RESPONSE
  // or a complete response within MAXIMUM_TIME_FOR_COMPLETE_RESPONSE_MS, then
  // run the failure action and call analytics. The resulting backend-to-frontend loop
  // will then cause a component rerendering with failure=true.
  useEffect(() => {
    if (isDone) {
      return;
    }

    if (streamingComplete && response) {
      return;
    }

    // we won't necessarily have a response in these scenarios
    if (isFailure || unanswerable || agentEscalation || interruptNonce) {
      return;
    }

    const totalTime = Date.now() - uiRenderingStartTime;

    const firstResponseTooSlow =
      !response && totalTime >= MAXIMUM_TIME_BEFORE_INITIAL_RESPONSE_MS;

    const completeResponseTooSlow =
      totalTime >= MAXIMUM_TIME_FOR_COMPLETE_RESPONSE_MS;

    if (firstResponseTooSlow || completeResponseTooSlow) {
      analytics.track(streamingFailedEvent, {
        live_answers_fetch_call_count: fetchCallCount,
        live_answers_client_fail_reason: firstResponseTooSlow
          ? 'first_response_slow'
          : 'complete_response_slow',
        live_answers_client_fail_total_time: totalTime,
        live_answers_no_initial_response_retried: noInitialResponseRetry,
        live_answers_final_timeout_retried: finalTimeoutRetry,
      });

      action({
        is_failure: true,
        cache_uuid: cacheUuid,
      });
      setClientSideFailure(true);
    }
  }, [
    action,
    agentEscalation,
    analytics,
    cacheUuid,
    fetchCallCount,
    finalTimeoutRetry,
    interruptNonce,
    isDone,
    isFailure,
    noInitialResponseRetry,
    response,
    streamingComplete,
    uiRenderingStartTime,
    unanswerable,
  ]);

  // ...and when failure=true is received in that rerendering, call onDone() to move to the failure branch.
  useEffect(() => {
    if (isDone) {
      return;
    }

    const totalTime = Date.now() - uiRenderingStartTime;

    if (isFailure && !interruptNonce) {
      analytics.action(inlineDataDoneCalled, {
        live_answers_done_type: 'failed',
        live_answers_client_fail_total_time: totalTime,
      });

      onDone({
        type: 'failure',
        // pass the original `inputMessage` back so the article-suggeste branch type can be used as a fallback
        message: inputMessage,
      });
      setClientSideFailure(true);
    }
  }, [
    isFailure,
    onDone,
    analytics,
    isDone,
    uiRenderingStartTime,
    interruptNonce,
    inputMessage,
  ]);

  // Stop streaming when an interrupt event is received
  useEffect(() => {
    const sub = events.observable
      .pipe(filter((e) => e.name === 'inline-data-interruption'))
      .subscribe((e) => {
        if (isDone) {
          return;
        }

        if (interruptNonce || !e.nonce) {
          return;
        }

        setInterruptNonce(e.nonce);

        const outcome = OUTCOME_INTERRUPTED;

        analytics.action(inlineDataDoneCalled, {
          live_answers_done_type: 'success',
          live_answers_done_outcome: outcome,
        });

        onDone({
          type: 'success',
          data: {outcome},
          extra: {
            outcome,
            value,
          },
        });
      });

    return () => sub.unsubscribe();
  }, [
    events,
    analytics,
    onDone,
    value,
    streamingComplete,
    isDone,
    interruptNonce,
  ]);

  // Emit result after interrupt has been handled
  useEffect(() => {
    if (isDone && interruptNonce) {
      events.emit({
        name: 'inline-data-interruption-result',
        nonce: interruptNonce,
      });
    }
  }, [events, isDone, interruptNonce]);

  // It's a bit weird, but we call the component as a function so we can check its return type.
  // Otherwise we can't tell if it returns null and not show an avatar.
  const content = ResponseContent({
    response,
    sources,
    unanswerable,
    isFailure: isFailure || clientSideFailure,
    // isComplete represents the backend streaming state being finished.
    isComplete: isComplete || !!interruptNonce,
    // isDone represents this inline data component having already called done(),
    // in which case we should never show a loading spinner if it errored out.
    isDone,
    agentEscalation,
    streamingConversationId,
    setRenderingComplete: renderingCompleteCallback,
  });

  // If the response content is null, don't show an avatar at all.
  // This should only occur in error states where Live Answers wants to hide itself entirely,
  // due to there being nothing to display while also not being the active effect in the UH conversation.
  if (content === null) {
    return <view.div data-testid="live-answers-null-content" />;
  }

  if (useNewDesign) {
    return <StripeAssistantResponse>{content}</StripeAssistantResponse>;
  }

  if (useDotComDesign) {
    return <SalesChatResponse>{content}</SalesChatResponse>;
  }

  // Failure and agent-escalation paths do not use the Stripe AI avatar.
  else if (agentEscalation || isFailure || clientSideFailure) {
    return <StripeBrandResponse>{content}</StripeBrandResponse>;
  } else {
    return (
      <SupportAIBrandResponse isLoading={!response && !streamingComplete}>
        {content}
      </SupportAIBrandResponse>
    );
  }
};
